import { Box, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyledInputLabel } from '../../../../../components/inputs/InputLabel';
import { StyledQuestionMark } from '../../../../../components/QuestionMark';
import { AccessibleSelect } from '../../../../../components/inputs/AccessibleSelect';
import { getIconSrc } from '../../../../../utils/getIconSrc';

type PaymentFieldsProps = {
  payment: string;
  paymentOptions: string[];
  disabled?: boolean;
  onPaymentChange: (event: SelectChangeEvent) => void;
  showPaymentUrl?: boolean;
  paymentUrl?: string;
  onPaymentUrlChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sanitizePaymentUrl?: (url: string) => string;
  paymentIcon?: string;
  baseAssetUrl?: string;
  hasI18nExtension?: boolean;
};

export const PaymentFields = ({
  payment,
  paymentOptions,
  disabled = false,
  onPaymentChange,
  showPaymentUrl = true,
  paymentUrl = '',
  onPaymentUrlChange,
  sanitizePaymentUrl = (url: string) => url,
  paymentIcon,
  baseAssetUrl = '',
  hasI18nExtension = false,
}: PaymentFieldsProps) => {
  const { t } = useTranslation();

  const paymentIconSrc = getIconSrc(paymentIcon, baseAssetUrl);

  return (
    <>
      <Box>
        <AccessibleSelect
          id="payment"
          label={
            <>
              Payment option <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </>
          }
          tooltip={
            <Tooltip title={t('agreements.tooltips.payment')} placement="top">
              <IconButton size="small" sx={{ marginLeft: '3px' }}>
                <StyledQuestionMark fontSize="small" />
              </IconButton>
            </Tooltip>
          }
          name="payment"
          value={paymentOptions.includes(payment) ? payment : ''}
          onChange={onPaymentChange}
          disabled={disabled}
          required
          endAdornment={
            paymentIconSrc ? (
              <img
                src={paymentIconSrc}
                alt=""
                aria-hidden="true"
                role="presentation"
                width="48"
                height="48"
                style={{ objectFit: 'contain', display: 'block' }}
              />
            ) : undefined
          }
        >
          {paymentOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </AccessibleSelect>
      </Box>
      {showPaymentUrl && (
        <Box mt={6}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StyledInputLabel htmlFor="payment_url">
              {payment === 'Contact Library'
                ? 'Email / URL (must begin with https://)'
                : 'Agreement URL/Payment Option Link'}
              {!hasI18nExtension && (
                <>
                  <span aria-hidden="true"> *</span>
                  <span className="sr-only">required</span>
                </>
              )}
            </StyledInputLabel>
            <Tooltip
              title={t(
                payment === 'Contact Library'
                  ? 'agreements.tooltips.urlContactLibrary'
                  : 'agreements.tooltips.url'
              )}
              placement="top"
            >
              <IconButton size="small" sx={{ marginLeft: '3px' }}>
                <StyledQuestionMark fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            id="payment_url"
            name="payment_url"
            sx={{
              backgroundColor:
                !paymentUrl && !hasI18nExtension ? '#fff4e5' : 'white',
              width: '100%',
            }}
            placeholder={
              payment === 'Contact Library'
                ? 'Must be a valid email / URL'
                : 'Must begin with https://...'
            }
            type="text"
            fullWidth
            onChange={onPaymentUrlChange ?? (() => {})}
            value={sanitizePaymentUrl(paymentUrl)}
            disabled={disabled}
          />
        </Box>
      )}
    </>
  );
};
